import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Checkbox, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography, useTheme, Button, ButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import { listAllBookings, listAllHotels, resetAllBookings, resetAllHotelsList } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { tableStyle } from "../mui-styles/table";
import { StatusChip } from "../../components/Chips";
import { PaginationFooter } from "../../components";
import { CalendarMonth, Download } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import moment from "moment";

const Bookings = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const startInputRef = useRef();
    const endInputRef = useRef();
    const [hotelId, setHotelId] = useState("");
    const [startDate, setStartDate] = useState(0);
    const [endDate, setEndDate] = useState(0);
    const [dateType, setDateType] = useState("checkin");
    const [localPage, setLocalPage] = useState(1);
    const [status, setStatus] = useState("all");
    const [bookingFrom, setBookingFrom] = useState("all");
    const [ota, setOta] = useState("all");
    const [type, setType] = useState("all");
    const allBookingFroms = [
        { name: "Direct booking", value: "direct_booking" },
        // { name: "Agent", value: "agent" },
        { name: "OTA", value: "ota" },
        { name: "Booking engine", value: "booking_engine" },
    ]

    useEffect(() => {
        dispatch(listAllHotels());
        dispatch(resetAllBookings());
        return () => {
            dispatch(resetAllHotelsList());
        }
    }, [dispatch]);

    // useEffect(() => {
    //     if (startDate && endDate) {
    //         dispatch(resetAllBookings());
    //         fetchMoreData({ startDate, endDate });
    //     }
    //     // eslint-disable-next-line
    // }, [startDate, endDate])

    const fetchMoreData = (page) => {
        if (!hotelId) return;

        if (page?.pageSize !== undefined) {
            setLocalPage(1);
        }

        let payload = {
            hotelId: hotelId,
            ...page,
            status: status === "all" ? undefined : status,
            type: type === "all" ? undefined : type,
            bookingFrom: bookingFrom === "all" ? undefined : bookingFrom
        };

        if (dateType === 'checkin') {
            payload.startDate = startDate;
            payload.endDate = endDate;
        } else {
            payload.bookedAtStartDate = startDate;
            payload.bookedAtEndDate = endDate;
        }

        dispatch(listAllBookings(payload));
    }

    const handleSubmit = () => {
        if (!hotelId) {
            toast.error("Please select a hotel");
            return;
        }
        dispatch(resetAllBookings());
        fetchMoreData();
    };

    const { bookingsList, hotels, pageNumber, pageSize, count } = useSelector(
        ({
            admin: {
                bookings: { list, pageNumber, pageSize, count },
                hotels,
            },
        }) => ({
            bookingsList: list,
            pageNumber, pageSize, count,
            hotels,
        })
    );

    const optionsList = hotels?.list?.map((el, idx) => ({
        label: el.name,
        id: el._id,
    }))

    const CustomFooter = () => (
        <PaginationFooter
            count={count}
            pageSize={pageSize}
            pageNumber={pageSize === count && count > 100 ? localPage : pageNumber}
            fetchMoreData={(page) => {
                if (
                    pageSize === count &&
                    count > 100 &&
                    page.pageNumber !== undefined &&
                    page.pageSize === undefined
                ) {
                    setLocalPage(page.pageNumber);
                } else {
                    fetchMoreData(page);
                }
            }}
        />
    );

    const displayedBookings = (pageSize === count && count > 100)
        ? bookingsList.slice((localPage - 1) * 100, localPage * 100)
        : bookingsList;

    const columns = [
        {
            field: "booked_at",
            headerName: "Booked on",
            width: 120,
            renderCell: ({row}) => (<small>{row?.booked_at ? moment.unix(row?.booked_at).format("DD-MM-YYYY") : "-"}</small>),
        },
        {
            field: "guest_name",
            headerName: "Guest info",
            width: 220,
            renderCell: ({ row: { guest_name, guest_email } }) => {
                return (
                    <Stack>
                        <Typography color={colors.greenAccent[400]}>
                            <b>{guest_name}</b>
                        </Typography>
                        <Typography fontSize={"small"}>
                            {guest_email || ""}
                        </Typography>
                    </Stack>
                )
            },
        },
        {
            field: "roomtypes",
            headerName: "Roomtype(s)",
            width: 220,
            renderCell: ({row}) => (<small>{row?.roomtypes?.join(', ') || "-"}</small>)
        },
        {
            field: "checkin_date",
            headerName: "Check-in date",
            width: 120,
            valueGetter: (params) => `${params.row.checkin_date ? moment.unix(params.row.checkin_date).format("YYYY-MM-DD") : "-"}`,
        },
        {
            field: "checkout_date",
            headerName: "Check-out date",
            width: 120,
            valueGetter: (params) => `${params.row.checkout_date ? moment.unix(params.row.checkout_date).format("YYYY-MM-DD") : "-"}`,
        },
        { 
            field: "total_price", 
            headerName: "Rate", 
            width: 100,
            renderCell: ({ row: { custom_price, total_price } }) => `${custom_price ? custom_price : total_price}`,
        },
        {
            field: "paid_amount",
            headerName: "Paid amount",
            width: 100,
            renderCell: ({ row }) => `${row?.paid_amount || 0}`,
        },
        {
            field: "total_rooms",
            headerName: "Total rooms",
            width: 100,
            renderCell: ({ row }) => `${row?.total_rooms || 0}`,
        },
        {
            field: "total_nights",
            headerName: "Total room nights",
            width: 140,
            renderCell: ({ row }) => `${row?.total_nights || 0}`,
        },
    ];

    return (
        <Box m="20px" mb={15}>
            <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                m="20px 0"
            >
                <Header title="BOOKINGS" subtitle="welcome to Bookings" />
            </Box>
            <Box display="flex" flexDirection="column" gap={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <ButtonGroup variant="outlined" size="small" sx={{ height: '40px', mr: 1 }}>
                        <Button 
                            onClick={() => setDateType('checkin')} 
                            variant={dateType === 'checkin' ? 'contained' : 'outlined'}
                            color={dateType === 'checkin' ? 'secondary' : 'inherit'}
                        >
                            CHECK-IN DATE
                        </Button>
                        <Button 
                            onClick={() => setDateType('booked')} 
                            variant={dateType === 'booked' ? 'contained' : 'outlined'}
                            color={dateType === 'booked' ? 'secondary' : 'inherit'}
                        >
                            BOOKED DATE
                        </Button>
                    </ButtonGroup>

                    <TextField
                        size="small"
                        type="date"
                        label="Start Date"
                        sx={{ width: 160 }}
                        InputLabelProps={{ shrink: true }}
                        value={startDate ? moment.unix(startDate).format("YYYY-MM-DD") : ""}
                        onChange={(newValue) => {
                            setStartDate(moment(newValue.target.value, "YYYY-MM-DD").startOf("day").unix());
                            if (endDate !== "" && moment(newValue.target.value).add(1, "days").format("YYYY-MM-DD") > moment.unix(endDate).format("YYYY-MM-DD")) {
                                setEndDate(moment(newValue.target.value, "YYYY-MM-DD").add(1, "days").endOf("day").unix());
                            }
                            if(!newValue.target.value) {
                                setStartDate(0)
                                setEndDate(0)
                            }
                        }}
                        inputRef={startInputRef}
                        onClick={() => startInputRef.current.showPicker()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarMonth sx={{ cursor: "pointer" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="End Date"
                        sx={{ width: 160 }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: moment.unix(startDate).format("YYYY-MM-DD") }}
                        value={endDate ? moment.unix(endDate).format("YYYY-MM-DD") : ""}
                        onChange={(newValue) => {
                            setEndDate(moment(newValue.target.value, "YYYY-MM-DD").endOf("day").unix())
                            if(!newValue.target.value) {
                                setStartDate(0)
                                setEndDate(0)
                            }
                        }}
                        inputRef={endInputRef}
                        onClick={() => endInputRef.current.showPicker()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarMonth sx={{ cursor: "pointer" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 120 }}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                    </TextField>

                    <TextField select size="small" label="Booking" value={bookingFrom} onChange={(e) => setBookingFrom(e.target.value)} sx={{ width: 140 }}>
                        <MenuItem value="all">All</MenuItem>
                        {allBookingFroms?.map((e) => (
                            <MenuItem value={e.value} key={e.value}>{e.name}</MenuItem>
                        ))}
                    </TextField>

                    <TextField select size="small" label="OTAs" value={ota} onChange={(e) => setOta(e.target.value)} sx={{ width: 120 }}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="makemytrip">MakeMyTrip</MenuItem>
                        <MenuItem value="agoda">Agoda</MenuItem>
                        <MenuItem value="goibibo">Goibibo</MenuItem>
                        <MenuItem value="booking.com">Booking.com</MenuItem>
                    </TextField>

                    <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ width: 100 }}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="b2b">B2B</MenuItem>
                        <MenuItem value="b2c">B2C</MenuItem>
                    </TextField>
                </Stack>
                
                <Stack direction={"row"} spacing={1} alignItems="center">
                    <Autocomplete
                        id="combo-box-demo"
                        size='small'
                        options={optionsList}
                        onChange={(e, value) => setHotelId(value?.id ?? "")}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => <TextField {...params} label="Filter by hotel" required />}
                        sx={{ width: 250 }}
                    />
                    <Button variant="contained" color="secondary" onClick={handleSubmit}>
                        Submit
                    </Button>
                </Stack>
            </Box>
            <Box
                m="8px 0 0 0"
                height="80vh"
                sx={tableStyle(colors)}
            >
                <DataGrid 
                    rows={displayedBookings} 
                    columns={columns} 
                    getRowId={(row) => row?._id} 
                    components={{ Footer: CustomFooter }}
                />
            </Box>
        </Box>
    );
};

export default Bookings;
